import { cn } from '@consigli/shadcn';
import { type BeamstarTurn } from '@consigli/types';
import { type ReactNode, useEffect, useRef } from 'react';
import { HiOutlineFlag } from 'react-icons/hi';

import { Badge } from '@/components';

import {
  counted,
  formatDateTime,
  formatDuration,
  formatTime,
  formatTokens,
  turnOutcomeMapping,
  workflowName,
} from '../format';
import { isFailedStep, StepList } from './step-list';

const Message = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn('flex flex-col gap-1 rounded-lg px-3 py-2', className)}>
    <span className="text-xs font-semibold text-muted-foreground">{label}</span>
    <p className="whitespace-pre-wrap text-sm">{children}</p>
  </div>
);

interface FlagBoxProps {
  flaggedAt: string;
  comment: string | null;
}

const FlagBox = ({ flaggedAt, comment }: FlagBoxProps) => (
  <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
    <div className="flex items-center gap-2 text-sm text-amber-900">
      <HiOutlineFlag className="size-4" />
      <span>Flagged {formatDateTime(flaggedAt)}</span>
    </div>
    <p className="whitespace-pre-wrap text-sm">{comment}</p>
  </div>
);

interface TurnCardProps {
  turn: BeamstarTurn;
  number: number;
  /** The turn a flag pointed at, which the reader scrolls to and marks. */
  isTarget: boolean;
}

export const TurnCard = ({ turn, number, isTarget }: TurnCardProps) => {
  const card = useRef<HTMLLIElement>(null);
  const failedSteps = turn.steps.filter(isFailedStep).length;

  useEffect(() => {
    if (isTarget) {
      card.current?.scrollIntoView({ block: 'start' });
    }
  }, [isTarget]);

  return (
    <li
      ref={card}
      className={cn(
        'flex flex-col gap-3 rounded-xl border p-4',
        isTarget && 'border-gray-900 ring-1 ring-gray-900',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">Turn {number}</span>
        <span>{formatTime(turn.startedAt)}</span>
        <Badge {...turnOutcomeMapping[turn.outcome]} />
        {turn.durationMs !== null && <span>{formatDuration(turn.durationMs)}</span>}
        <span>
          {formatTokens(turn.inputTokens)} in · {formatTokens(turn.cachedInputTokens)} cached ·{' '}
          {formatTokens(turn.outputTokens)} out
        </span>
      </div>
      <Message
        label={turn.workflowId ? `Workflow: ${workflowName(turn.workflowId)}` : 'Message'}
        className="bg-muted"
      >
        {turn.message}
      </Message>
      {turn.error ? (
        <Message label="Error" className="bg-red-50 text-red-900">
          {turn.error}
        </Message>
      ) : (
        <Message label="Beamstar">
          {turn.reply || <span className="text-muted-foreground">No reply.</span>}
        </Message>
      )}
      {turn.flaggedAt && <FlagBox flaggedAt={turn.flaggedAt} comment={turn.flagComment} />}
      {turn.steps.length > 0 && (
        <details open={failedSteps > 0 || isTarget} className="rounded-lg border">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
            {counted(turn.steps.length, 'step', 'steps')}
            {failedSteps > 0 && `, ${failedSteps} failed`}
          </summary>
          <StepList steps={turn.steps} />
        </details>
      )}
    </li>
  );
};
